import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { Menu, MenuItem, IconButton, Tooltip } from '@mui/material';
import { MoreVert, Comment } from '@mui/icons-material';

import { AbsenceCalendarData, isAdmin } from '../data/useAbsenceCalendar';
import { Employee, EnrichedDepartment, EnrichedEmployee } from '../../Structure/types';
import { useUser } from '../../../../toolympus/userContext/UserContext';
import { ABSENCE_KIND_LETTERS, AbsenceEvent, getEventColor } from '../data/types';
import { DepartmentStaffingWarning } from '../data/useStaffingWarnings';

interface AbsenceCalendarProps {
  data: AbsenceCalendarData;
  showEmployeeEventList: (e: Employee, events: AbsenceEvent[]) => void;
}

export const AbsenceCalendar: React.FC<AbsenceCalendarProps> = (props) => {
  const { data, showEmployeeEventList } = props;
  const { calendar, orgStructure, orgStructureManagement, eventBuckets, notesBuckets, staffingWarnings } = data;

  const { user } = useUser();
  const adminMode = isAdmin(user);

  // --- Dropdown Menu State ---
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEntity, setSelectedEntity] = useState<{
    type: 'department' | 'employee';
    data: EnrichedDepartment | EnrichedEmployee;
  } | null>(null);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    type: 'department' | 'employee',
    entityData: EnrichedDepartment | EnrichedEmployee
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedEntity({ type, data: entityData });
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedEntity(null);
  };

  const handleHideEntity = () => {
    if (!selectedEntity) return;

    orgStructureManagement.hideOrUnhide.startEditing({
      item: selectedEntity.type === 'employee'
        ? selectedEntity.data as EnrichedEmployee
        : selectedEntity.data as EnrichedDepartment,
      kind: selectedEntity.type,
      operation: 'hide',
    });
    
    handleCloseMenu();
  };

  const handleEditEvent = (e: React.MouseEvent, eventObj: AbsenceEvent) => {
    e.stopPropagation();
    data.editItem.startEditing({ ...eventObj });
  };

  const days = useMemo(() => {
    const arr: moment.Moment[] = [];
    const current = calendar.startDateMoment.clone();
    const end = calendar.endDateMoment;
    
    while (current.isSameOrBefore(end, 'day')) {
      arr.push(current.clone());
      current.add(1, 'day');
    }
    return arr;
  }, [calendar.startDateMoment, calendar.endDateMoment]);

  const rows = useMemo(() => {
    if (!orgStructure?.departments) return [];

    const flatRows: Array<{
      type: 'department' | 'employee';
      id: string;
      label: string;
      isMe?: boolean;
      data: EnrichedEmployee | EnrichedDepartment;
    }> = [];

    orgStructure.departments.forEach((dept) => {
      if (dept.is_hidden) return;

      flatRows.push({
        type: 'department',
        id: `dept-${dept._id}`,
        label: dept.title,
        data: dept,
      });

      let emps = dept.employees || [];
      emps = emps.filter((e) => !e.is_hidden);

      if (dept.head) {
        const headId = dept.head._id;
        const headEmp = emps.find((e) => e._id === headId) || dept.head;
        const otherEmps = emps.filter((e) => e._id !== headId);
        emps = [headEmp, ...otherEmps];
      }

      emps.forEach((emp) => {
        flatRows.push({
          type: 'employee',
          id: `emp-${dept._id}-${emp._id}`,
          label: `${emp.first_name} ${emp.last_name || ''}`.trim(),
          isMe: user?._id === emp.user_id,
          data: emp,
        });
      });
    });

    return flatRows;
  }, [orgStructure, user?._id]);

  const today = moment();

  const [dragSelection, setDragSelection] = useState<{
    empId: string;
    startDate: string;
    endDate: string;
  } | null>(null);

  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging && dragSelection) {
        const start = moment(dragSelection.startDate);
        const end = moment(dragSelection.endDate);
        const isReversed = start.isAfter(end);
        
        const finalStart = isReversed ? dragSelection.endDate : dragSelection.startDate;
        const finalEnd = isReversed ? dragSelection.startDate : dragSelection.endDate;

        data.newItem.start({
          owner_id: dragSelection.empId,
          start_date: finalStart,
          end_date: finalEnd,
        });

        setDragSelection(null);
        setIsDragging(false);
      }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging, dragSelection, data.newItem]);

  const handleMouseDown = (empId: string, dateStr: string) => {
    setIsDragging(true);
    setDragSelection({
      empId,
      startDate: dateStr,
      endDate: dateStr,
    });
  };

  const handleMouseEnter = (empId: string, dateStr: string) => {
    if (isDragging && dragSelection && dragSelection.empId === empId) {
      setDragSelection({ ...dragSelection, endDate: dateStr });
    }
  };

  return (
    <Container>
      <CalendarTable>
        <thead>
          <tr>
            <NameHeaderCell>
              {data.calendar.currentMonthMoment.clone().format('MMM YYYY')}
            </NameHeaderCell>
            {days.map((day) => {
              const isWeekend = day.isoWeekday() > 5;
              const isToday = day.isSame(today, 'day');
              
              return (
                <DayHeaderCell key={day.format('YYYY-MM-DD')} isWeekend={isWeekend} isToday={isToday}>
                  {day.format('D')}
                </DayHeaderCell>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isDept = row.type === 'department';
            return (
              <Tr key={row.id} isDept={isDept} isMe={row.isMe}>
                <NameCell isDept={isDept} isMe={row.isMe}>
                  <NameContainer>
                    <NameText isDept={isDept} isMe={row.isMe} title={row.label}>
                      {row.label}
                    </NameText>
                    
                    <ActionIconBtn 
                      size="small" 
                      onClick={(e) => handleOpenMenu(e, row.type, row.data)}
                    >
                      <MoreVert fontSize="small" />
                    </ActionIconBtn>
                  </NameContainer>
                </NameCell>
                
                {days.map((day) => {
                  const dateStr = day.format('YYYY-MM-DD');
                  const isWeekend = day.isoWeekday() > 5;
                  const isToday = day.isSame(today, 'day');

                  let eventObj: AbsenceEvent | undefined;
                  let noteObj: AbsenceEvent | undefined;
                  let isStart = false;
                  let isEnd = false;
                  let staffingWarning: DepartmentStaffingWarning | undefined;

                  if (!isDept) {
                    const emp = row.data as EnrichedEmployee;
                    eventObj = eventBuckets[emp._id]?.[dateStr];
                    noteObj = notesBuckets[emp._id]?.[dateStr];
                    
                    if (eventObj) {
                      isStart = eventObj.start_date === dateStr;
                      isEnd = eventObj.end_date === dateStr;
                    }
                  } else {
                    staffingWarning = staffingWarnings.find(
                      (x) => x.department_id === row.data._id && x.date === dateStr
                    );
                  }

                  let isHighlighted = false;

                  if (!isDept && dragSelection && dragSelection.empId === (row.data as EnrichedEmployee)._id) {
                    const cellDate = day.clone();
                    const selStart = moment(dragSelection.startDate);
                    const selEnd = moment(dragSelection.endDate);
                    
                    const minDate = selStart.isBefore(selEnd) ? selStart : selEnd;
                    const maxDate = selStart.isAfter(selEnd) ? selStart : selEnd;

                    isHighlighted = cellDate.isBetween(minDate, maxDate, 'day', '[]');
                  }

                  return (
                    <DayCell 
                      key={dateStr} 
                      isWeekend={isWeekend} 
                      isToday={isToday}
                      isDept={isDept}
                      isHighlighted={isHighlighted}
                      onMouseDown={() => {
                        if (!isDept) handleMouseDown((row.data as EnrichedEmployee)._id, dateStr);
                      }}
                      onMouseEnter={() => {
                        if (!isDept) handleMouseEnter((row.data as EnrichedEmployee)._id, dateStr);
                      }}
                      style={{ cursor: (!isDept && !eventObj && !noteObj) ? 'pointer' : 'default' }}
                    >
                      {eventObj && (
                        <EventBlock
                          kind={eventObj.kind}
                          isStart={isStart}
                          isEnd={isEnd}
                          isCancelled={!!eventObj.cancelled_at}
                          onClick={(e) => handleEditEvent(e, eventObj!)}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <EventLetter>{ABSENCE_KIND_LETTERS[eventObj.kind]}</EventLetter>
                        </EventBlock>
                      )}

                      {noteObj && (
                        <Tooltip title={noteObj.comment || 'Note'} arrow placement="top">
                          <NoteIconBlock
                            onClick={(e) => handleEditEvent(e, noteObj!)}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <Comment style={{ fontSize: 10, color: '#555' }} />
                          </NoteIconBlock>
                        </Tooltip>
                      )}

                      {!!isDept && !!staffingWarning && (
                        <Tooltip title="мало людей в офисе">
                          <StaffingWarning />
                        </Tooltip>
                      )}
                    </DayCell>
                  );
                })}
              </Tr>
            );
          })}
        </tbody>
      </CalendarTable>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        closeAfterTransition
      >
        {adminMode && (
          <MenuItem onClick={handleHideEntity}>
            скрыть {selectedEntity?.type === 'department' ? 'отдел' : 'сотрудника'}
          </MenuItem>
        )}
        {selectedEntity?.type === 'employee' && (
          <MenuItem onClick={() => {
            data.absenceStats.showFor(selectedEntity?.data as any);
            handleCloseMenu();
          }}>
            статистика
          </MenuItem>
        )}
        {adminMode && selectedEntity?.type === 'employee' && (
          <MenuItem onClick={() => {
            data.extraQuotas.showFor(selectedEntity?.data as any);
            handleCloseMenu();
          }}>
            дополнительные квоты
          </MenuItem>
        )}
        {selectedEntity?.type === 'employee' && (
          <MenuItem onClick={() => {
            showEmployeeEventList(
              selectedEntity?.data as any,
              data.events?.data?.filter((e) => e.owner_id === selectedEntity?.data._id) || []
            );
            handleCloseMenu();
          }}>
            списком
          </MenuItem>
        )}
      </Menu>
    </Container>
  );
};

// -----------------------------------------------------------------------------
// Styled Components
// -----------------------------------------------------------------------------

const Container = styled.div`
  width: 100%;
  overflow: auto;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  background-color: #fff;
  position: relative;
`;

const CalendarTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`;

const NameHeaderCell = styled.th`
  width: 250px;
  min-width: 250px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  background-color: #fafafa;
  position: sticky;
  top: 0;
  z-index: 3;
  left: 0; 
  text-align: left;
  font-size: 0.65rem;
  padding-left: 12px;
  font-weight: 400;
  text-transform: capitalize;
  color: #666;
`;

const DayHeaderCell = styled.th<{ isWeekend: boolean; isToday: boolean }>`
  border: 1px solid ${(p) => (p.isToday ? '#90caf9' : '#e0e0e0')};
  padding: 4px;
  text-align: center;
  font-size: 0.65rem;
  font-weight: ${(p) => (p.isToday ? 700 : 400)};
  color: ${(p) => {
    if (p.isToday) return '#1565c0';
    if (p.isWeekend) return '#d32f2f';
    return '#666';
  }};
  background-color: ${(p) => {
    if (p.isToday) return '#bbdefb';
    if (p.isWeekend) return '#f5f5f5';
    return '#fafafa';
  }};
  position: sticky;
  top: 0;
  z-index: 2;
  background-clip: padding-box;
  ${(p) => p.isToday && `
    box-shadow: inset 0 2px 0 #1976d2;
  `}
`;

const Tr = styled.tr<{ isDept: boolean; isMe?: boolean }>`
  background-color: ${(p) => {
    if (p.isMe) return '#e8f4fd';
    if (p.isDept) return '#eceff1';
    return 'transparent';
  }};

  &:nth-of-type(even) {
    background-color: ${(p) => {
      if (p.isMe) return '#dfedf9';
      if (p.isDept) return '#eceff1';
      return '#fafafa'; 
    }};
  }

  &:hover {
    background-color: ${(p) => {
      if (p.isDept) return '#eceff1';
      if (p.isMe) return '#d0e6f9';
      return 'rgba(25, 118, 210, 0.175)';
    }};
  }
  
  &:hover td button {
    opacity: 1;
    pointer-events: auto;
  }
`;

const NameCell = styled.td<{ isDept: boolean; isMe?: boolean }>`
  border: 1px solid #e0e0e0;
  padding: 0 4px 0 ${(p) => (p.isDept ? '12px' : '24px')};
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: inherit;
  background-clip: padding-box;
  ${(p) => p.isMe && `
    box-shadow: inset 4px 0 0 #1976d2;
  `}
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
`;

const NameText = styled.span<{ isDept: boolean; isMe?: boolean }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.85rem;
  font-weight: ${(p) => (p.isDept ? 600 : p.isMe ? 700 : 400)};
  color: ${(p) => (p.isMe ? '#0d47a1' : 'inherit')};
`;

const ActionIconBtn = styled(IconButton)`
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  margin-right: -4px; 
`;

const DayCell = styled.td<{ isWeekend: boolean; isToday: boolean; isDept: boolean; isHighlighted?: boolean }>`
  border: 1px solid #e0e0e0;
  height: 32px;
  position: relative;
  padding: 0;

  background-color: ${(p) => {
    if (p.isHighlighted) return 'rgba(25, 118, 210, 0.25)';
    if (p.isDept) return 'transparent';
    if (p.isToday) return 'rgba(25, 118, 210, 0.15)';
    if (p.isWeekend) return 'rgba(0, 0, 0, 0.04)';
    return 'transparent';
  }};

  ${(p) => p.isToday && `
    box-shadow: inset 1px 0 0 rgba(25, 118, 210, 0.35), inset -1px 0 0 rgba(25, 118, 210, 0.35);
  `}

  user-select: none;
`;

export const EventBlock = styled.div<{ kind: string; isStart: boolean; isEnd: boolean; isCancelled?: boolean }>`
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 0;
  right: 0;
  background-color: ${(p) => getEventColor(p.kind, p.isCancelled)};
  cursor: pointer;
  z-index: 1;
  
  border-top-left-radius: ${(p) => (p.isStart ? '4px' : '0')};
  border-bottom-left-radius: ${(p) => (p.isStart ? '4px' : '0')};
  border-top-right-radius: ${(p) => (p.isEnd ? '4px' : '0')};
  border-bottom-right-radius: ${(p) => (p.isEnd ? '4px' : '0')};

  margin-left: ${(p) => (p.isStart ? '4px' : '0')};
  margin-right: ${(p) => (p.isEnd ? '4px' : '0')};

  &:hover {
    filter: brightness(0.9);
  }
`;

export const EventLetter = styled.span`
  position: absolute;
  top: 1px;
  left: 3px;
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  color: rgba(0, 0, 0, 0.5);
  user-select: none;
`;

export const NoteIconBlock = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #fff;
    transform: scale(1.1);
  }
`;

export const StaffingWarning = styled.div`
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 0;
  right: 0;
  background-color: rgba(244, 67, 54, 0.75);
  cursor: pointer;
  border-radius: 4px;
  margin-left: 4px;
  margin-right: 4px;
`;